import { Children, cloneElement, isValidElement, useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// antd <Collapse>/<Collapse.Panel> -> MUI Accordion. Collapse coordinates which
// panels are open (activeKey, controlled or uncontrolled; accordion=single-open)
// and injects expanded/onToggle into each Panel. NOTE: distinct from MUI's own
// <Collapse in={...}> transition — only antd Collapse imports map here.

const toArray = (k?: string | string[] | number | number[]): string[] =>
  k === undefined ? [] : (Array.isArray(k) ? k : [k]).map(String);

// React.Children.map prefixes keys with ".$"; strip it to recover the antd key.
const rawKey = (child: ReactElement, fallback: number): string => {
  const k = child.key;
  if (k == null) return String(fallback);
  return String(k).replace(/^\.\$/, "");
};

export interface CollapseItem {
  key: string | number;
  label: ReactNode;
  children?: ReactNode;
  extra?: ReactNode;
  collapsible?: "header" | "disabled" | "icon";
}

export interface CollapseProps {
  activeKey?: string | string[] | number | number[];
  defaultActiveKey?: string | string[] | number | number[];
  onChange?: (key: string | string[]) => void;
  accordion?: boolean;
  bordered?: boolean;
  ghost?: boolean;
  /** antd v5 items API (alternative to Collapse.Panel children). */
  items?: CollapseItem[];
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export const Collapse = ({ activeKey, defaultActiveKey, onChange, accordion, items, children, style, className }: CollapseProps) => {
  const controlled = activeKey !== undefined;
  const [internal, setInternal] = useState<string[]>(toArray(defaultActiveKey));
  const active = controlled ? toArray(activeKey) : internal;

  const toggle = (key: string) => {
    const isOpen = active.includes(key);
    const next = isOpen ? active.filter((k) => k !== key) : accordion ? [key] : [...active, key];
    if (!controlled) setInternal(next);
    onChange?.(accordion ? next[0] ?? "" : next);
  };

  if (items) {
    return (
      <div style={style} className={className}>
        {items.map((it) => (
          <Panel
            key={String(it.key)}
            header={it.label}
            extra={it.extra}
            collapsible={it.collapsible}
            _expanded={active.includes(String(it.key))}
            _onToggle={() => toggle(String(it.key))}
          >
            {it.children}
          </Panel>
        ))}
      </div>
    );
  }

  return (
    <div style={style} className={className}>
      {Children.map(children, (child, i) => {
        if (!isValidElement(child)) return child;
        const key = rawKey(child as ReactElement, i);
        return cloneElement(child as ReactElement<PanelProps>, {
          _expanded: active.includes(key),
          _onToggle: () => toggle(key),
        });
      })}
    </div>
  );
};

export interface PanelProps {
  header?: ReactNode;
  extra?: ReactNode;
  children?: ReactNode;
  collapsible?: "header" | "disabled" | "icon";
  forceRender?: boolean;
  style?: CSSProperties;
  className?: string;
  // injected by Collapse (not part of the public antd API):
  _expanded?: boolean;
  _onToggle?: () => void;
}

const Panel = ({ header, extra, children, collapsible, forceRender, _expanded, _onToggle, style, className }: PanelProps) => {
  // antd renders Panel content LAZILY (nothing until the first expand, mounted
  // thereafter); MUI's AccordionDetails mounts children immediately. That gap is
  // why the publish modal hung: it mounts one ReactDiffViewer per draft field, so
  // opening it char-diffed every field while all panels were still collapsed
  // (~47M diff cells on one investment). Match antd. `forceRender` is antd's
  // opt-out for callers that need collapsed content mounted.
  const [hasExpanded, setHasExpanded] = useState(false);
  useEffect(() => {
    if (_expanded) setHasExpanded(true);
  }, [_expanded]);
  // `_expanded` is included so content appears on the expanding frame rather than
  // one frame late; hasExpanded then keeps it mounted once collapsed again.
  const shouldRender = forceRender || hasExpanded || _expanded;

  return (
    <Accordion
      expanded={_expanded ?? false}
      onChange={() => collapsible !== "disabled" && _onToggle?.()}
      disableGutters
      style={style}
      className={className}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div style={{ display: "flex", flex: 1, justifyContent: "space-between", alignItems: "center" }}>
          <span>{header}</span>
          {extra ? <span onClick={(e) => e.stopPropagation()}>{extra}</span> : null}
        </div>
      </AccordionSummary>
      <AccordionDetails>{shouldRender ? children : null}</AccordionDetails>
    </Accordion>
  );
};

Collapse.Panel = Panel;

export default Collapse;
